<template>
  <article class="total-costs" :class="{ '-compact': compact, '-collapsed': collapsed }">
    <template v-if="!collapsed">
      <div v-if="seats > 0" class="total-costs__entry" >
        <div>
          <div class="total-costs__product">
            simcision {{ plan.label }} <template v-if="compact">x {{ totalSeats }} <template v-if="years > 1">({{ $t("count.years", years) }})</template></template>
          </div>
          <span class="total-costs__entry__detail" v-if="!compact">
            {{ $t("labels.countUsers", totalSeats) }}<template v-if="years > 1">, {{ $t("count.years", years) }}</template>
          </span>
        </div>

        <div class="total-costs__price">
          {{ formattedBasePrice }}<br />
          <small v-if="!includeVat && !compact">
            {{ $t("labels.taxesNotIncluded") }}
          </small>
        </div>
      </div>

      <template v-if="plan.upgradeable">
        <div v-for="upgrade of selectedUpgradePlans" class="total-costs__entry">
          <div>
            <div class="total-costs__product">
              <template v-if="compact">
                {{ $t(`upgrades.${upgrade.id}.name`) }}
              </template>
              <template v-else>
                {{ $t("labels.upgrade") }}
              </template>
            </div>
            <span v-if="!compact" class="total-costs__entry__detail">
              {{ $t(`upgrades.${upgrade.id}.name`) }}<template v-if="years > 1">, {{ $t("count.years", years) }}</template>
            </span>
          </div>

          <div class="total-costs__price">
            {{ formatCurrency(upgrade.price * years) }}<br />
            <small v-if="!includeVat && !compact">
              {{ $t("labels.taxesNotIncluded") }}
            </small>
          </div>
        </div>
      </template>

      <template v-if="days != 365">
        <hr class="mv-2">
        <div class="total-costs__entry pb-1 -small">
          <div>
            <div class="total-costs__product">
              {{ $t("labels.subtotal") }}
            </div>
          </div>

          <div class="total-costs__price">
            {{ formatCurrency(summedPrice) }}
          </div>
        </div>

        <div class="total-costs__entry -small">
          <div>
            <div class="total-costs__product">
              {{ $t("labels.partialYearReduction", { days: days, discount: formatNumber(-discount * 100) }) }}
            </div>
          </div>

          <div class="total-costs__price">
            {{ formatCurrency(percentageReduction) }}
          </div>
        </div>
      </template>

      <template v-if="includeVat && totalVAT > 0" >
        <hr class="mv-2">
        <div class="total-costs__entry pb-1 -small">
          <div>
            <div class="total-costs__product">
              {{ $t("labels.subtotal") }}
            </div>
          </div>

          <div class="total-costs__price">
            {{ formatCurrency(totalPrice) }}
          </div>
        </div>
        <div class="total-costs__entry -small">
          <div>
            <div class="total-costs__product">
              {{ $t("labels.vat") }} ({{ vat * 100 }}&hairsp;%)
            </div>
          </div>

          <div class="total-costs__price">
            {{ formatCurrency(totalVAT) }}
          </div>
        </div>
      </template>

      <hr>
    </template>

    <div class="total-costs__total">
      <div v-if="!collapsed">
        <strong class="total-costs__total__title">
          {{ $t("labels.total") }}
        </strong>
      </div>
      <div class="total-costs__price">
        <strong class="total-costs__total__price">
          {{ formattedTotalPrice }}&hairsp;
        </strong>
        <br />
        <div v-if="summedPrice > 0" class="total-costs__extra">
          <span v-if="perYear && years == 1">
            {{ $t(`per.${paymentPeriod}`) }}
          </span>
          <span v-if="!includeVat" class="ml-2">
            {{
              $t("labels.taxesNotIncluded")
            }}
          </span>
        </div>
      </div>
    </div>

    <footer class="total-costs__footer">
      <slot></slot>
    </footer>
  </article>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { usePlans } from "@manager/plans/plans.store";
import type { LicensePlan } from "@manager/plans/plans.store";

const props = withDefaults(defineProps<{
  plan: LicensePlan;
  paymentPeriod?: "year" | "month";
  seats?: number;
  includeVat: boolean;
  vat: number;
  days?: number | null;
  upgrades?: string[];
  compact?: boolean;
  perYear?: boolean;
  collapsed?: boolean;
  years?: number;
}>(), {
  seats: 0,
  paymentPeriod: "year",
  includeVat: false,
  vat: 0.19,
  days: 365,
  upgrades: () => [],
  compact: false,
  perYear: true,
  collapsed: false,
  years: 1
});

const { locale } = useI18n();
const planStore = usePlans();

// ========================
// SEATS

const totalSeats = computed(() => {
  return props.seats;
});

// ========================
// UPGRADES

const selectedUpgradePlans = computed(() => {
  return props.upgrades?.map((id) => planStore.upgradePlans[id]);
});

const totalUpgradesPrice = computed(() => {
  return selectedUpgradePlans.value.reduce((acc, upgradePlan) => {
    return acc + upgradePlan.price * props.years;
  }, 0);
});

// ========================

const percentage = computed(() => {
  if (props.days == null) return 1;
  return props.days / 365;
});
const discount = computed(() => {
  return 1 - percentage.value;
});
const percentageReduction = computed(() => {
  return -summedPrice.value * discount.value;
});

const basePrice = computed(() => {
  const price = props.plan?.price ?? 0;
  return price * props.seats * props.years;
});
const totalPriceWithVAT = computed(() => {
  let price = totalPrice.value;
  if (props.includeVat) {
    return price + totalVAT.value;
  }
  return price;
});

const totalVAT = computed(() => {
  return Math.floor(totalPrice.value * props.vat * 100) / 100;
});

const summedPrice = computed(() => {
  let price = basePrice.value;

  if (props.plan.upgradeable) {
    price += totalUpgradesPrice.value;
  }

  return price;
});

const totalPrice = computed(() => {
  return summedPrice.value + percentageReduction.value;
});

const formattedBasePrice = computed(() => {
  return formatCurrency(basePrice.value);
});
const formattedTotalPrice = computed(() => {
  return formatCurrency(totalPriceWithVAT.value);
});


// ========================
// Helper functions

function formatCurrency(num: number) {
  return formatNumber(num, {
    style: "currency",
    currency: "EUR"
  });
}
function formatNumber(num: number, options = {}) {
  num = Math.floor(num * 100) / 100;
  return (
    num
      .toLocaleString(locale.value, options)
      .replaceAll(/\s/g, " ")
      // eslint-disable-next-line no-irregular-whitespace
      .replace(/,00 /, ",— ")
  );
}
</script>

<style lang="scss">
.total-costs {
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: min-content;
  font-size: 16px;

  &__heading {
    padding: 0 1rem;
  }

  &__entry,
  &__total {
    display: grid;
    grid-template-columns: 1fr max-content;
    padding: 0.5rem 0;
    gap: 1rem;
  }
  &.-collapsed &__total {
    grid-template-columns: 1fr;
  }

  &__product {
    font-weight: 400;
  }

  &__entry {
    &.-small {
      padding: 0.25rem 0;
      font-size: 0.9rem;
    }
    &__detail {
      font-size: 1em;
      color: var(--color-text-medium);
    }
  }

  &__total {
    background-color: var(--color-background-medium);
    padding-top: 0;
    padding-bottom: 1rem;

    background-color: transparent;

    &__title {
      font-size: 1.5rem;
      font-weight: 700;
      line-height: 3rem;
    }
    &__price {
      font-size: 1.75rem;
      line-height: 3rem;
    }
  }

  &.-compact &__total__title {
    font-size: 1.25rem;
    line-height: 2rem;
  }
  &.-compact &__total__price {
    font-size: 1.25rem;
    line-height: 2rem;
  }

  &__price {
    text-align: right;
  }
  &.-collapsed &__price {
    text-align: left;
  }

  &__extra {
    font-size: 1rem;
    color: var(--color-dark);
  }

  &__batchnotice {
    padding: 0.5rem 1rem;
  }

  &__footer {
    padding: 0 1rem;
  }
}
</style>
